import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CountUp from 'react-countup';

const Stats = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');
  const [isInView, setIsInView] = useState(false);

  const stats = [
    { number: 26, suffix: "+", label: t('stats.label1') },
    { number: 170, suffix: "+", label: t('stats.label2') },
    { number: 15, suffix: "+", label: t('stats.label3') },
    { number: 99.8, suffix: "%", label: t('stats.label4') },
  ];

  return (
    <section className="relative py-0 overflow-hidden font-quicksand transition-colors duration-300">
      {/* Top border */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-auxis-gold/20 to-transparent" />

      <div className="bg-auxis-off-white dark:bg-auxis-charcoal py-20 md:py-28 relative transition-colors duration-300">
        {/* Subtle glow behind stats */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[200px] bg-auxis-gold/10 dark:bg-auxis-gold/5 blur-[100px] rounded-full mix-blend-multiply dark:mix-blend-screen" />

        <div className="container-wide relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
                onViewportEnter={() => setIsInView(true)}
                className="text-center relative group"
              >
                {/* Vertical divider (not on first) */}
                {index > 0 && (
                  <div className="hidden lg:block absolute -left-6 top-1/2 -translate-y-1/2 w-px h-20 bg-gradient-to-b from-transparent via-auxis-charcoal/10 dark:via-white/10 to-transparent" />
                )}

                <div className="mb-2">
                  <span className="text-5xl md:text-6xl lg:text-7xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-auxis-gold via-auxis-gold-dark to-auxis-charcoal/80 dark:to-white/80 drop-shadow-sm">
                    {isInView ? (
                      <CountUp
                        start={0}
                        end={stat.number}
                        duration={3.5}
                        useEasing={true}
                        separator=","
                        decimals={stat.number % 1 !== 0 ? 1 : 0}
                        decimal="."
                        suffix={stat.suffix}
                        delay={0.1}
                      />
                    ) : stat.number + stat.suffix}
                  </span>
                </div>
                <p className="text-sm uppercase tracking-[0.15em] font-bold text-auxis-charcoal/60 dark:text-gray-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Scrolling text marquee */}
      <div className="bg-auxis-gold py-4 overflow-hidden shadow-inner">
        <div className="flex animate-slide-left whitespace-nowrap">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 mx-6 flex-shrink-0">
              {[
                t('stats.marquee1'), 
                t('stats.marquee2'), 
                t('stats.marquee3'), 
                t('stats.marquee4'), 
                t('stats.marquee5'), 
                t('stats.marquee6'), 
                t('stats.marquee7'), 
                t('stats.marquee8')
              ].map((text, j) => (
                <span key={j} className="flex items-center gap-4 group cursor-default">
                  <span className="text-sm font-extrabold tracking-[0.15em] text-white/90 uppercase group-hover:text-white transition-colors">{text}</span>
                  <span className="w-1.5 h-1.5 rounded-full bg-white/40 group-hover:bg-white" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom border */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-dark-border to-transparent" />
    </section>
  );
};

export default Stats;